import {type RefObject, useEffect} from 'react';

/**
 * Closes a menu on a click anywhere outside it, or on Escape.
 *
 * Listeners on the document, which is the one thing a menu needs that React's own event handlers
 * cannot give it. Only attached while open, so a closed menu costs nothing.
 *
 * `composedPath()`, not `contains(event.target)`: the app renders inside a shadow root, and by the
 * time an event reaches the document its target has been retargeted to the shadow HOST — which is
 * never inside the menu. `contains` made every click, including one on an option, look "outside".
 */
export const useDismiss = (
    root: RefObject<HTMLElement | null>,
    open: boolean,
    onDismiss: () => void,
): void => {
    useEffect(() => {
        if (!open) return;

        const onPointerDown = (event: PointerEvent) => {
            const inside = root.current !== null && event.composedPath().includes(root.current);

            if (!inside) onDismiss();
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onDismiss();
        };

        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [root, open, onDismiss]);
};
